import { configureStore } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import languageReducer from "./languageSlice";
import drawerReducer from "./drawerSlice";
import guidReducer from "./guidSlice";
import phoneReducer from "./phoneSlice";
import userInfoReducer from "./userInfoSlice";
import customerDataReducer from "./customerDataSlice";
import { applicationControlReducer } from "./applicationControlSlice";
import { eVoucherReducer } from "./evoucherSlice";
import { countryPhoneCodeReducer } from "./countryCodeSlice";

/**
 * @typedef {Object} LanguageState
 * @property {string} [language]
 * @property {any} [texts]
 * @property {any} [dateFormats]
 */

/**
 * @typedef {Object} UserInfoState
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} countryCode
 * @property {string} email
 * @property {string} phoneNo
 * @property {string | null} status
 * @property {boolean} registered
 */

/**
 * @typedef {Object} CustomerDataState
 * @property {Object} customer
 * @property {string} customer.name
 * @property {string} customer.custCode
 * @property {string | null} customer.fiscalCode
 * @property {string} customer.address
 * @property {string} customer.country
 * @property {string} customer.county
 * @property {string} customer.street
 * @property {string | null} customer.zipCode
 * @property {Object} consignee
 * @property {string | null} consignee.name
 * @property {string | null} consignee.custCode
 * @property {string | null} consignee.fiscalCode
 * @property {string | null} consignee.address
 * @property {string | null} consignee.country
 * @property {string | null} consignee.county
 * @property {string | null} consignee.street
 * @property {string | null} consignee.zipCode
 * @property {Object} contactPerson
 * @property {string} contactPerson.contactPersonId
 * @property {string} contactPerson.personName
 * @property {string} contactPerson.phone
 * @property {string} contactPerson.email
 */

/**
 * @typedef {Object} ApplicationControlState
 * @property {boolean} busy
 */

/**
 * @typedef {Object} EVoucherState
 * @property {Object} selectedRows
 * @property {number} selectedRows.serialNo
 * @property {string} selectedRows.externalSerialNo
 * @property {string} selectedRows.orderGuid
 * @property {string} selectedRows.orderNo
 * @property {Date} selectedRows.orderDate
 * @property {string} selectedRows.orderType
 * @property {string} selectedRows.orderStatus
 * @property {string} selectedRows.orderStatusName
 * @property {string | null} selectedRows.customerCode
 * @property {string} selectedRows.customerName
 * @property {string} selectedRows.voucherStatus
 * @property {Date} selectedRows.lastChanged
 * @property {string} selectedRows.voucherStatusName
 * @property {string} selectedRows.voucherType
 * @property {string} selectedRows.voucherTypeDescription
 * @property {number} selectedRows.amount
 * @property {string} selectedRows.currency
 * @property {Date} selectedRows.expDate
 */

/**
 * @typedef {Object} CountryPhoneCodeState
 * @property {Object[]} countryPhoneCodes
 * @property {string} countryPhoneCodes[].label
 * @property {string} countryPhoneCodes[].value
 * @property {string} countryPhoneCodes[].name
 * @property {string} countryPhoneCodes[].phonePrefix
 */

/**
 * @typedef {Object} RootState
 * @property {LanguageState} language
 * @property {string} guid
 * @property {boolean} drawer
 * @property {string} phone
 * @property {UserInfoState} userInfo
 * @property {CustomerDataState} customerData
 * @property {ApplicationControlState} applicationControl
 * @property {EVoucherState} eVoucher
 * @property {CountryPhoneCodeState} countryPhoneCode
 */

export const store = configureStore({
  reducer: {
    language: languageReducer,
    drawer: drawerReducer,
    guid: guidReducer,
    phone: phoneReducer,
    userInfo: userInfoReducer,
    customerData: customerDataReducer,
    applicationControl: applicationControlReducer,
    eVoucher: eVoucherReducer,
    countryPhoneCode: countryPhoneCodeReducer,
  },
});

/**
 *
 * @template T
 * @param {function(RootState): T} selector
 * @returns {T}
 */

export const useAppSelector = (selector) => {
  return useSelector((state) => selector(state));
};
